import type { FC, ReactNode } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';

import { RightSectionContext } from '../../../context/right-section';
import type { ElectoralCodeLevel } from '../helper';
import type { actionComponent } from '../right-panel/utils';
import { CodePeekPane } from './CodePeekPane';
import type { CodePeekEntry, CodePeekValue } from './use-code-peek';
import { CodePeekContext } from './use-code-peek';

const COMPONENT_BY_LEVEL: Record<ElectoralCodeLevel, actionComponent> = {
	state: 'state-details',
	lga: 'lga-details',
	ward: 'ward-details',
	pollingUnit: 'pu-details',
};

const TITLE_BY_LEVEL: Record<ElectoralCodeLevel, (code: string) => string> = {
	state: (c) => `State ( ${c} )`,
	lga: (c) => `LGA ( ${c} )`,
	ward: (c) => `Ward ( ${c} )`,
	pollingUnit: (c) => `Polling Unit ( ${c} )`,
};

interface CodePeekProviderProps {
	children: ReactNode;
}

export const CodePeekProvider: FC<CodePeekProviderProps> = ({ children }) => {
	const rightPanel = useContext(RightSectionContext);
	const [stack, setStack] = useState<CodePeekEntry[]>([]);

	const current = stack.length > 0 ? stack[stack.length - 1] : null;

	const openPeek = useCallback((level: ElectoralCodeLevel, code: string) => {
		setStack((prev) => {
			const last = prev[prev.length - 1];
			if (last?.level === level && last.code === code) return prev;
			return [...prev, { level, code }];
		});
	}, []);

	const closePeek = useCallback(() => {
		setStack([]);
	}, []);

	const goToHistory = useCallback((index: number) => {
		setStack((prev) => (index < 0 || index >= prev.length ? prev : prev.slice(0, index + 1)));
	}, []);

	const promoteToPrimary = useCallback(() => {
		if (!current || !rightPanel) {
			setStack([]);
			return;
		}
		rightPanel.callSection({
			action: 'view',
			component: COMPONENT_BY_LEVEL[current.level],
			title: TITLE_BY_LEVEL[current.level](current.code),
			data: { code: current.code },
		});
		setStack([]);
	}, [current, rightPanel]);

	const value: CodePeekValue = useMemo(
		() => ({
			current,
			history: stack,
			openPeek,
			closePeek,
			goToHistory,
			promoteToPrimary,
		}),
		[current, stack, openPeek, closePeek, goToHistory, promoteToPrimary]
	);

	return (
		<CodePeekContext.Provider value={value}>
			{children}
			<CodePeekPane />
		</CodePeekContext.Provider>
	);
};
